import { CancelToken } from "axios";
import api from "src/spintr/SpintrApi";

const nodesBaseUrl = "/api/v1/Nodes";
const nodeTypesBaseUrl = "/api/v1/NodeTypes";
const nodeManagersBaseUrl = "/api/v1/NodeManagers";
const unitsBaseUrl = "/api/v1/Units";

export type NodeProjection = {
    id: number;
    instanceId: number;
    parentId?: number | undefined;
    nodeTypeId: string;
    referencedUberId?: number | undefined;
    referencedUberType?: number | undefined;
    referencedUberName?: string | undefined;
    referencedDepartmentIsQualityHubEnabled?: boolean | undefined;
    name: string;
    contentStatus: number;
    createdOn: Date;
    createdBy?: number | undefined;
    editedOn?: Date | undefined;
    editedBy?: number | undefined;
};

export type NodeMembershipProjection = NodeProjection & {
    isManager: boolean;
    isExecutionNode: boolean;
};

export type NodeTypeProjection = {
    id: string;
    instanceId: number;
    name: string;
    description?: string | undefined;
    contentStatus: number;
    allowsReference: boolean;
    defaultReferencedUberType?: number | undefined;
    isSynthetic: boolean;
    createdOn: Date;
    editedOn?: Date | undefined;
};

export type NodeManagerProjection = {
    id: string;
    nodeId: number;
    userId: number;
    role: number;
    validFrom?: Date | undefined;
    validTo?: Date | undefined;
    createdOn: Date;
    createdBy?: number | undefined;
    editedOn?: Date | undefined;
    editedBy?: number | undefined;
};

export type NodeQueryParams = {
    skip?: number;
    take?: number;
    search?: string;
    parentId?: number;
    nodeTypeId?: string;
    referencedUberId?: number;
    contentStatus?: number;
};

export type NodeModel = {
    parentId?: number | undefined;
    nodeTypeId: string;
    referencedUberId?: number | undefined;
    referencedUberType?: number | undefined;
    name: string;
    contentStatus: number;
};

export type NodeTypeQueryParams = {
    skip?: number;
    take?: number;
    search?: string;
    contentStatus?: number;
};

export type NodeTypeModel = {
    name: string;
    description?: string | undefined;
    contentStatus: number;
    allowsReference: boolean;
    defaultReferencedUberType?: number | undefined;
    isSynthetic: boolean;
};

export type NodeManagerQueryParams = {
    nodeId?: number;
    userId?: number;
    role?: number;
    skip?: number;
    take?: number;
};

export type NodeManagerModel = {
    nodeId: number;
    userId: number;
    role: number;
    validFrom?: Date | undefined;
    validTo?: Date | undefined;
};

export type NodelessUnitQueryParams = {
    skip?: number;
    take?: number;
    searchPhrase?: string;
    excludeDepartments?: boolean;
    excludeOffices?: boolean;
    excludeCompanies?: boolean;
    qualityHubEnabled?: boolean;
};

export type NodelessUnitProjection = {
    id: number;
    imageUrl?: string | undefined;
    info?: string | undefined;
    name: string;
    type: number;
};

function fixNodeDates(node: Omit<NodeProjection, "createdOn" | "editedOn"> & {
    createdOn: string;
    editedOn?: string | null | undefined;
}): NodeProjection {
    return {
        ...node,
        createdOn: new Date(node.createdOn),
        editedOn: node.editedOn ? new Date(node.editedOn) : undefined,
    };
}

function fixNodeMembershipDates(node: Omit<NodeMembershipProjection, "createdOn" | "editedOn"> & {
    createdOn: string;
    editedOn?: string | null | undefined;
}): NodeMembershipProjection {
    return {
        ...fixNodeDates(node),
        isManager: node.isManager,
        isExecutionNode: node.isExecutionNode,
    };
}

function fixNodeTypeDates(nodeType: Omit<NodeTypeProjection, "createdOn" | "editedOn"> & {
    createdOn: string;
    editedOn?: string | null | undefined;
}): NodeTypeProjection {
    return {
        ...nodeType,
        createdOn: new Date(nodeType.createdOn),
        editedOn: nodeType.editedOn ? new Date(nodeType.editedOn) : undefined,
    };
}

function fixNodeManagerDates(nodeManager: Omit<NodeManagerProjection, "createdOn" | "editedOn" | "validFrom" | "validTo"> & {
    createdOn: string;
    editedOn?: string | null | undefined;
    validFrom?: string | null | undefined;
    validTo?: string | null | undefined;
}): NodeManagerProjection {
    return {
        ...nodeManager,
        createdOn: new Date(nodeManager.createdOn),
        editedOn: nodeManager.editedOn ? new Date(nodeManager.editedOn) : undefined,
        validFrom: nodeManager.validFrom ? new Date(nodeManager.validFrom) : undefined,
        validTo: nodeManager.validTo ? new Date(nodeManager.validTo) : undefined,
    };
}

export async function queryNodesAsync(
    params: NodeQueryParams = {},
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<NodeProjection>> {
    const response = await api.get<Spintr.QueryEnvelope<Omit<NodeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>>(nodesBaseUrl, {
        params,
        cancelToken,
    });

    return {
        ...response.data,
        items: response.data.items.map(fixNodeDates),
    };
}

export async function queryMyNodesAsync(
    cancelToken?: CancelToken,
): Promise<NodeMembershipProjection[]> {
    const response = await api.get<Array<Omit<NodeMembershipProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>>(`${nodesBaseUrl}/my`, {
        cancelToken,
    });

    return response.data.map(fixNodeMembershipDates);
}

export async function findNodeAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<NodeProjection> {
    const response = await api.get<Omit<NodeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(`${nodesBaseUrl}/${id}`, {
        cancelToken,
    });

    return fixNodeDates(response.data);
}

export async function createNodeAsync(
    model: NodeModel,
    cancelToken?: CancelToken,
): Promise<NodeProjection> {
    const response = await api.post<Omit<NodeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(nodesBaseUrl, model, {
        cancelToken,
    });

    return fixNodeDates(response.data);
}

export async function updateNodeAsync(
    id: number,
    model: NodeModel,
    cancelToken?: CancelToken,
): Promise<NodeProjection> {
    const response = await api.put<Omit<NodeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(`${nodesBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return fixNodeDates(response.data);
}

export async function deleteNodeAsync(
    id: number,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(`${nodesBaseUrl}/${id}`, {
        cancelToken,
    });
}

export async function setDepartmentQualityHubEnabledAsync(
    departmentId: number,
    isEnabled: boolean,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.put<void>(
        `${unitsBaseUrl}/${departmentId}/qualityhub-enabled`,
        { isEnabled },
        { cancelToken },
    );
}

export async function queryNodelessUnitsAsync(
    params: NodelessUnitQueryParams = {},
    cancelToken?: CancelToken,
): Promise<NodelessUnitProjection[]> {
    const response = await api.get<NodelessUnitProjection[]>(`${unitsBaseUrl}/nodeless`, {
        params,
        cancelToken,
    });

    return response.data;
}

export async function queryNodeTypesAsync(
    params: NodeTypeQueryParams = {},
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<NodeTypeProjection>> {
    const response = await api.get<Spintr.QueryEnvelope<Omit<NodeTypeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>>(nodeTypesBaseUrl, {
        params,
        cancelToken,
    });

    return {
        ...response.data,
        items: response.data.items.map(fixNodeTypeDates),
    };
}

export async function findNodeTypeAsync(
    id: string,
    cancelToken?: CancelToken,
): Promise<NodeTypeProjection> {
    const response = await api.get<Omit<NodeTypeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(`${nodeTypesBaseUrl}/${id}`, {
        cancelToken,
    });

    return fixNodeTypeDates(response.data);
}

export async function createNodeTypeAsync(
    model: NodeTypeModel,
    cancelToken?: CancelToken,
): Promise<NodeTypeProjection> {
    const response = await api.post<Omit<NodeTypeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(nodeTypesBaseUrl, model, {
        cancelToken,
    });

    return fixNodeTypeDates(response.data);
}

export async function updateNodeTypeAsync(
    id: string,
    model: NodeTypeModel,
    cancelToken?: CancelToken,
): Promise<NodeTypeProjection> {
    const response = await api.put<Omit<NodeTypeProjection, "createdOn" | "editedOn"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
    }>(`${nodeTypesBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return fixNodeTypeDates(response.data);
}

export async function deleteNodeTypeAsync(
    id: string,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(`${nodeTypesBaseUrl}/${id}`, {
        cancelToken,
    });
}

export async function queryNodeManagersAsync(
    params: NodeManagerQueryParams = {},
    cancelToken?: CancelToken,
): Promise<Spintr.QueryEnvelope<NodeManagerProjection>> {
    const response = await api.get<Spintr.QueryEnvelope<Omit<NodeManagerProjection, "createdOn" | "editedOn" | "validFrom" | "validTo"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
        validFrom?: string | null | undefined;
        validTo?: string | null | undefined;
    }>>(nodeManagersBaseUrl, {
        params,
        cancelToken,
    });

    return {
        ...response.data,
        items: response.data.items.map(fixNodeManagerDates),
    };
}

export async function findNodeManagerAsync(
    id: string,
    cancelToken?: CancelToken,
): Promise<NodeManagerProjection> {
    const response = await api.get<Omit<NodeManagerProjection, "createdOn" | "editedOn" | "validFrom" | "validTo"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
        validFrom?: string | null | undefined;
        validTo?: string | null | undefined;
    }>(`${nodeManagersBaseUrl}/${id}`, {
        cancelToken,
    });

    return fixNodeManagerDates(response.data);
}

export async function createNodeManagerAsync(
    model: NodeManagerModel,
    cancelToken?: CancelToken,
): Promise<NodeManagerProjection> {
    const response = await api.post<Omit<NodeManagerProjection, "createdOn" | "editedOn" | "validFrom" | "validTo"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
        validFrom?: string | null | undefined;
        validTo?: string | null | undefined;
    }>(nodeManagersBaseUrl, model, {
        cancelToken,
    });

    return fixNodeManagerDates(response.data);
}

export async function updateNodeManagerAsync(
    id: string,
    model: NodeManagerModel,
    cancelToken?: CancelToken,
): Promise<NodeManagerProjection> {
    const response = await api.put<Omit<NodeManagerProjection, "createdOn" | "editedOn" | "validFrom" | "validTo"> & {
        createdOn: string;
        editedOn?: string | null | undefined;
        validFrom?: string | null | undefined;
        validTo?: string | null | undefined;
    }>(`${nodeManagersBaseUrl}/${id}`, model, {
        cancelToken,
    });

    return fixNodeManagerDates(response.data);
}

export async function deleteNodeManagerAsync(
    id: string,
    cancelToken?: CancelToken,
): Promise<void> {
    await api.delete<void>(`${nodeManagersBaseUrl}/${id}`, {
        cancelToken,
    });
}
